/**
 * Ask Asper page-session tracking for Mixpanel.
 *
 * A session starts when AskAsper mounts and ends once (single-fire) on logout,
 * browser/tab close, auth timeout, or in-app navigation away from Ask Asper.
 * Aggregated metrics are included on session_end.
 *
 * `time_spent_seconds` counts only while the browser tab is visible (Page Visibility
 * API), so background tabs left open do not inflate session duration.
 */

import { trackEventMixpanel } from 'utils/mixpanel';
import {
    ASK_ASPER_SESSION_END,
    ASK_ASPER_SESSION_END_REASON,
    ASK_ASPER_SESSION_START,
    type AskAsperSessionEndReason,
} from 'utils/mixpanelEventNames';

export type AskAsperTrackedThread = {
    thread_id: string;
    title: string;
};

type SessionState = {
    active: boolean;
    ended: boolean;
    /** Milliseconds accumulated while the tab was visible. */
    accumulatedVisibleMs: number;
    /** Start of the current visible interval; null while the tab is hidden. */
    visibleSince: number | null;
    /** Bumped on each start so a deferred navigate-end from Strict Mode remount is ignored. */
    generation: number;
    /** Existing threads opened/continued this session (keyed by thread_id). */
    existingThreads: Map<string, AskAsperTrackedThread>;
    /** Threads created this session (keyed by thread_id). */
    newThreads: Map<string, AskAsperTrackedThread>;
    questionsAsked: number;
    exportsCount: number;
};

const state: SessionState = {
    active: false,
    ended: false,
    accumulatedVisibleMs: 0,
    visibleSince: null,
    generation: 0,
    existingThreads: new Map(),
    newThreads: new Map(),
    questionsAsked: 0,
    exportsCount: 0,
};

let pendingNavigateEndTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityListenerAttached = false;

const clearPendingNavigateEnd = () => {
    if (pendingNavigateEndTimer != null) {
        clearTimeout(pendingNavigateEndTimer);
        pendingNavigateEndTimer = null;
    }
};

const flushVisibleTime = (): void => {
    if (state.visibleSince == null) return;
    state.accumulatedVisibleMs += Date.now() - state.visibleSince;
    state.visibleSince = null;
};

const getVisibleTimeSpentSeconds = (): number => {
    flushVisibleTime();
    return Math.max(0, Math.round(state.accumulatedVisibleMs / 1000));
};

const handleVisibilityChange = (): void => {
    if (!isAskAsperSessionActive()) return;

    if (document.visibilityState === 'hidden') {
        flushVisibleTime();
        return;
    }

    if (state.visibleSince == null) {
        state.visibleSince = Date.now();
    }
};

const attachVisibilityTracking = (): void => {
    if (visibilityListenerAttached) return;
    document.addEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerAttached = true;
};

const detachVisibilityTracking = (): void => {
    if (!visibilityListenerAttached) return;
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerAttached = false;
};

const toThreadList = (map: Map<string, AskAsperTrackedThread>): AskAsperTrackedThread[] =>
    Array.from(map.values());

export const isAskAsperSessionActive = (): boolean => state.active && !state.ended;

export const startAskAsperSession = (): void => {
    clearPendingNavigateEnd();
    detachVisibilityTracking();
    flushVisibleTime();

    state.generation += 1;
    state.active = true;
    state.ended = false;
    state.accumulatedVisibleMs = 0;
    state.visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
    state.existingThreads = new Map();
    state.newThreads = new Map();
    state.questionsAsked = 0;
    state.exportsCount = 0;

    attachVisibilityTracking();
    trackEventMixpanel(ASK_ASPER_SESSION_START);
};

/**
 * Record a thread opened or continued this session.
 * - `isNew: true` → new thread created in this session
 * - otherwise → existing thread accessed (skipped if already tracked as new)
 */
export const recordAskAsperThreadAccessed = (
    threadId?: string | null,
    title?: string | null,
    options?: { isNew?: boolean },
): void => {
    if (!isAskAsperSessionActive() || !threadId) return;

    const normalizedTitle = (title ?? '').trim();
    const isNew = options?.isNew === true;

    if (isNew) {
        // Prefer new over existing if the same id somehow appears in both.
        state.existingThreads.delete(threadId);
        const prev = state.newThreads.get(threadId);
        state.newThreads.set(threadId, {
            thread_id: threadId,
            title: normalizedTitle || prev?.title || '',
        });
        return;
    }

    // Continuing a thread created earlier in this same session stays under new_threads.
    if (state.newThreads.has(threadId)) {
        if (normalizedTitle) {
            const prev = state.newThreads.get(threadId)!;
            state.newThreads.set(threadId, { ...prev, title: normalizedTitle });
        }
        return;
    }

    const prev = state.existingThreads.get(threadId);
    state.existingThreads.set(threadId, {
        thread_id: threadId,
        title: normalizedTitle || prev?.title || '',
    });
};

/** Update title for a thread already tracked as new or existing (no-op otherwise). */
export const updateAskAsperThreadTitle = (threadId?: string | null, title?: string | null): void => {
    if (!isAskAsperSessionActive() || !threadId) return;
    const normalizedTitle = (title ?? '').trim();
    if (!normalizedTitle) return;

    if (state.newThreads.has(threadId)) {
        state.newThreads.set(threadId, { thread_id: threadId, title: normalizedTitle });
        return;
    }
    if (state.existingThreads.has(threadId)) {
        state.existingThreads.set(threadId, { thread_id: threadId, title: normalizedTitle });
    }
};

export const recordAskAsperQuestionAsked = (): void => {
    if (!isAskAsperSessionActive()) return;
    state.questionsAsked += 1;
};

/** Count export actions in this session (pass artifact count for multi-select). */
export const recordAskAsperExport = (count: number = 1): void => {
    if (!isAskAsperSessionActive() || count <= 0) return;
    state.exportsCount += count;
};

/**
 * Ends the Ask Asper session and fires ask_asper_session_end once.
 * Safe to call when no session is active (no-op). Call BEFORE localStorage.clear()
 * so Mixpanel can still read email/token.
 */
export const endAskAsperSession = (
    reason: AskAsperSessionEndReason,
    extras?: { navigated_to?: string },
): void => {
    if (!state.active || state.ended) return;

    clearPendingNavigateEnd();
    detachVisibilityTracking();

    state.ended = true;
    state.active = false;

    const timeSpentSeconds = getVisibleTimeSpentSeconds();
    const existingThreads = toThreadList(state.existingThreads);
    const newThreads = toThreadList(state.newThreads);

    const properties: Record<string, unknown> = {
        end_reason: reason,
        time_spent_seconds: timeSpentSeconds,
        questions_asked: state.questionsAsked,
        exports_count: state.exportsCount,
        existing_threads_accessed: existingThreads,
        new_threads_created: newThreads,
        existing_threads_count: existingThreads.length,
        new_threads_count: newThreads.length,
    };

    if (reason === ASK_ASPER_SESSION_END_REASON.NAVIGATE) {
        properties.navigated_to = extras?.navigated_to || window.location.pathname;
    }

    trackEventMixpanel(ASK_ASPER_SESSION_END, properties, { keepalive: true });
};

/**
 * Schedule session_end with navigate reason after unmount. Deferred so React Strict Mode
 * remount can cancel it via startAskAsperSession(). Captures the destination path after
 * the router has updated.
 */
export const scheduleAskAsperSessionEndOnNavigate = (): void => {
    if (!isAskAsperSessionActive()) return;

    clearPendingNavigateEnd();
    const generation = state.generation;
    pendingNavigateEndTimer = setTimeout(() => {
        pendingNavigateEndTimer = null;
        if (state.generation !== generation) return;
        endAskAsperSession(ASK_ASPER_SESSION_END_REASON.NAVIGATE, {
            navigated_to: window.location.pathname,
        });
    }, 0);
};

export { ASK_ASPER_SESSION_END_REASON };
